import { BrowserRouter, Routes, Route, useLocation, useParams } from "react-router-dom";
import "@/App.css";
import ClubPredictorPage from "./pages/ClubPredictorPage";
import SuccessPage from "./pages/SuccessPage";
import ClubAdminPage from "./pages/ClubAdminPage";
import AdminLoginPage from "./pages/AdminLoginPage";
import SuperAdminPage from "./pages/SuperAdminPage";
import TermsPage from "./pages/TermsPage";
import PrivacyPage from "./pages/PrivacyPage";
import NotFoundPage from "./pages/NotFoundPage";
import OnboardPage from "./pages/OnboardPage";
import OnboardSuccessPage from "./pages/OnboardSuccessPage";
import WelcomePage from "./pages/WelcomePage";
import DemoCreatePage from "./pages/DemoCreatePage";
import DemoPage from "./pages/DemoPage";
import OpenGolfPredictorPage from "./pages/OpenGolfPredictorPage";
import OpenGolfDemoPage from "./pages/OpenGolfDemoPage";
import OpenGolfSuccessPage from "./pages/OpenGolfSuccessPage";
import GaaFinalPredictorPage from "./pages/GaaFinalPredictorPage";
import GaaFinalDemoPage from "./pages/GaaFinalDemoPage";
import GaaFinalSuccessPage from "./pages/GaaFinalSuccessPage";
import ChatEmbed from "./pages/ChatEmbed";
import StripeSetupGuide from "./pages/StripeSetupGuide";
import StripeConnectedPage from "./pages/StripeConnectedPage";
import { HelpChat } from "./components/HelpChat";

function HelpChatWrapper() {
  const location = useLocation();
  const path = location.pathname;

  // Don't show on super admin or embed routes
  if (path.startsWith('/super-admin')) return null;
  if (path.startsWith('/embed/')) return null;

  // Hide the chat bubble on every All-Ireland (GAA) surface — public predictor,
  // welcome, admin, success, and the GAA demo. The GAA journey is a single tight
  // one-page flow and the bubble covers the sticky bottom Pay CTA on mobile.
  if (path.startsWith('/allireland2026/')) return null;
  if (path.startsWith('/demo/gaa/')) return null;

  // Extract club slug if on a club page
  const clubMatch = path.match(/^\/worldcup2026\/([^/]+)/);
  const clubSlug = clubMatch ? clubMatch[1] : '';

  // Hide the chat widget on mobile on demo pages — the form is already short
  // and the widget crowds the viewport. Still show on desktop.
  const isDemoPage = path.startsWith('/demo');
  if (isDemoPage) {
    return (
      <div className="hidden md:block">
        <HelpChat pageContext={path} clubSlug={clubSlug} />
      </div>
    );
  }

  return <HelpChat pageContext={path} clubSlug={clubSlug} />;
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          {/* Club pages - direct link shared by clubs */}
          <Route path="/worldcup2026/:slug" element={<ClubPredictorPage />} />
          <Route path="/worldcup2026/:slug/terms" element={<TermsPage />} />
          <Route path="/worldcup2026/:slug/privacy" element={<PrivacyPage />} />
          <Route path="/worldcup2026/:slug/success" element={<SuccessPage />} />
          <Route path="/worldcup2026/:slug/admin" element={<ClubAdminPage />} />
          <Route path="/worldcup2026/:slug/welcome" element={<WelcomePage />} />
          <Route path="/worldcup2026/:slug/login" element={<AdminLoginPage />} />
          
          {/* Super Admin - your management dashboard */}
          <Route path="/super-admin" element={<SuperAdminPage />} />
          
          {/* Onboarding */}
          <Route path="/onboard" element={<OnboardPage />} />
          <Route path="/onboard/success" element={<OnboardSuccessPage />} />
          <Route path="/onboard/stripe-connected" element={<StripeConnectedPage />} />
          
          {/* Demo */}
          <Route path="/demo/create" element={<DemoCreatePage />} />
          <Route path="/demo/:demoId" element={<DemoPage />} />

          {/* Open Golf 2026 — multi-sport (Phase 1: placeholder pages) */}
          <Route path="/opengolf2026/:slug" element={<OpenGolfPredictorPage />} />
          <Route path="/opengolf2026/:slug/success" element={<OpenGolfSuccessPage />} />
          <Route path="/opengolf2026/:slug/admin" element={<ClubAdminPage />} />
          <Route path="/opengolf2026/:slug/welcome" element={<WelcomePage />} />
          <Route path="/opengolf2026/:slug/login" element={<AdminLoginPage />} />
          <Route path="/demo/golf/:demoId" element={<OpenGolfDemoPage />} />

          {/* All-Ireland Final 2026 — Kerry v Mayo score predictor */}
          <Route path="/allireland2026/:slug" element={<GaaFinalPredictorPage />} />
          <Route path="/allireland2026/:slug/success" element={<GaaFinalSuccessPage />} />
          <Route path="/allireland2026/:slug/admin" element={<ClubAdminPage />} />
          <Route path="/allireland2026/:slug/welcome" element={<WelcomePage />} />
          <Route path="/allireland2026/:slug/login" element={<AdminLoginPage />} />
          <Route path="/demo/gaa/:demoId" element={<GaaFinalDemoPage />} />

          {/* Embeddable chatbot for external sites (e.g. targetfunder.com) */}
          <Route path="/embed/chat" element={<ChatEmbed />} />

          {/* Help / guides */}
          <Route path="/help/stripe-setup" element={<StripeSetupGuide />} />
          
          {/* 404 / Landing */}
          <Route path="/" element={<NotFoundPage />} />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
        <HelpChatWrapper />
      </BrowserRouter>
    </div>
  );
}

export default App;
